import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, ChevronRight } from 'lucide-react';
import axios from '../../lib/axios';
import { API, useLanguage, useIsland } from '../../App';
import { useCurrency } from '../../App';

/**
 * PersonalizedRecommendations
 * 
 * Shows personalized listing recommendations based on recent search history.
 * - Mobile: Horizontal scroll carousel with "peeking" 3rd card
 * - Desktop: Compact grid (4 columns)
 * 
 * Shows when:
 * - User has recent searches AND no active search query
 * - No category is currently selected (browsing all categories)
 */
const PersonalizedRecommendations = ({ 
  recentSearches = [], 
  currentSearchQuery = '',
  currentCategory = '',
  onClearHistory 
}) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { currentIsland } = useIsland();
  const { currency, formatDualPrice } = useCurrency();
  const scrollRef = useRef(null);
  
  const [listings, setListings] = useState([]);
  const [matchedTerms, setMatchedTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to track last fetched terms to avoid re-fetching the same data
  const lastFetchedRef = useRef('');
  // Track if history was just cleared to allow fresh fetch on new search
  const historyWasClearedRef = useRef(false);

  // Create a stable string representation of recent searches for comparison
  // Only use non-empty terms
  const currentTermsKey = recentSearches.slice(0, 3).filter(t => t && t.trim()).join(',');
  
  // Show conditions (calculated separately from fetch logic)
  const hasRecentSearches = recentSearches.length > 0;
  const noActiveSearch = !currentSearchQuery;
  const noCategorySelected = !currentCategory || currentCategory === 'all';
  const shouldShow = hasRecentSearches && noActiveSearch && noCategorySelected && !dismissed;

  // Memoized fetch function
  const fetchPersonalized = useCallback(async (termsKey) => {
    if (!termsKey || termsKey.length < 2) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({ terms: termsKey, limit: '6' });
      if (currentIsland && currentIsland !== 'all') {
        params.append('island', currentIsland);
      }
      
      const response = await axios.get(`${API}/listings/personalized?${params}`);
      
      if (response.data.listings?.length >= 1) {
        setListings(response.data.listings);
        setMatchedTerms(response.data.matched_terms || []);
      } else {
        setListings([]);
      }
      lastFetchedRef.current = termsKey;
    } catch (err) {
      console.error('Failed to fetch personalized listings:', err);
      setError(err);
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [currentIsland]);

  // Main effect: fetch when terms change AND conditions are right
  useEffect(() => {
    // No terms = nothing to fetch
    if (!currentTermsKey) {
      return;
    }
    
    // Only fetch when display conditions are met (no active search, no category filter)
    if (!noActiveSearch || !noCategorySelected || dismissed) {
      return;
    }
    
    // Check if this is a fresh search after clearing history
    const isFreshSearchAfterClear = historyWasClearedRef.current;
    
    // Check if terms changed from what we last fetched
    const termsChanged = currentTermsKey !== lastFetchedRef.current;
    
    // Fetch if: terms changed OR fresh search after clear
    if (!termsChanged && !isFreshSearchAfterClear) {
      return;
    }
    
    // Reset the cleared flag since we're now fetching
    if (isFreshSearchAfterClear) {
      historyWasClearedRef.current = false;
    }
    
    fetchPersonalized(currentTermsKey);
  }, [currentTermsKey, noActiveSearch, noCategorySelected, dismissed, fetchPersonalized]);

  // Format price using dual currency
  const formatPrice = (price, priceCurrency) => {
    if (!price) return t('price_on_request_short') || 'Price TBD';
    const prices = formatDualPrice(price, priceCurrency || 'USD');
    return currency === 'XCG' ? prices.xcg : prices.usd;
  };

  // Handle card click
  const handleCardClick = (listing) => {
    navigate(`/listing/${listing.slug || listing.id}`);
  };

  // Handle dismiss
  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('personalized_dismissed', currentTermsKey || 'true');
  };

  // Reset dismissed state when search terms change (user did a new search)
  const prevTermsRef = useRef(currentTermsKey);
  useEffect(() => {
    if (!currentTermsKey) {
      prevTermsRef.current = '';
      return;
    }
    
    if (currentTermsKey !== prevTermsRef.current) {
      const dismissedForTerms = sessionStorage.getItem('personalized_dismissed');
      
      // Reset dismissed if terms changed
      if (dismissed && dismissedForTerms && dismissedForTerms !== currentTermsKey) {
        setDismissed(false);
        sessionStorage.removeItem('personalized_dismissed');
      }
      
      prevTermsRef.current = currentTermsKey;
    }
  }, [currentTermsKey, dismissed]);

  // Check if was dismissed this session (only on mount)
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('personalized_dismissed');
    if (wasDismissed && wasDismissed !== 'undefined') {
      setDismissed(true);
    }
  }, []);

  // Listen for recentSearchesCleared event from SearchBar or other components
  useEffect(() => {
    const handleSearchHistoryCleared = () => {
      // Reset local state to allow fresh fetch on next search
      setListings([]);
      setMatchedTerms([]);
      lastFetchedRef.current = '';
      historyWasClearedRef.current = true;
      setDismissed(false);
      sessionStorage.removeItem('personalized_dismissed');
    };
    
    window.addEventListener('recentSearchesCleared', handleSearchHistoryCleared);
    return () => window.removeEventListener('recentSearchesCleared', handleSearchHistoryCleared);
  }, []);

  // Handle clear history - reset state to allow fresh fetch on next search
  const handleClearHistory = useCallback(() => {
    // Call parent's clear function
    onClearHistory?.();
    // Clear local state
    setListings([]);
    setMatchedTerms([]);
    // Reset refs to allow fresh fetch
    lastFetchedRef.current = '';
    historyWasClearedRef.current = true;
    // Reset dismissed state
    setDismissed(false);
    sessionStorage.removeItem('personalized_dismissed');
  }, [onClearHistory]);

  // Don't render if conditions not met
  if (!shouldShow || !currentTermsKey) {
    return null;
  }
  
  // Show loading state or no results gracefully
  if (loading) {
    return null; // Or could show a skeleton
  }
  
  if (listings.length < 1) {
    return null;
  }

  // Get display term for header
  const displayTerm = matchedTerms[0] || recentSearches[0] || '';

  return (
    <div 
      className="mb-4 bg-secondary/20 dark:bg-secondary/10 rounded-2xl p-3 sm:p-4 border border-border/40"
      data-testid="personalized-recommendations"
    >
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-2 mb-3">
        <div className="flex items-start sm:items-center gap-2 min-w-0 flex-1">
          <Sparkles className="w-4 h-4 text-amber-500 fill-amber-500/20 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div className="min-w-0">
            {/* Mobile: Stack title and subtitle */}
            <h3 className="text-xs sm:text-sm font-semibold text-foreground leading-tight">
              {t('personalized_for_you') || 'Possibly interesting for you'}
            </h3>
            <span className="text-muted-foreground font-normal text-[10px] sm:text-xs block sm:inline sm:ml-0">
              {t('based_on_your_searches') || 'based on your searches'}
            </span>
          </div>
        </div>
        
        {/* Actions - Dismiss button only */}
        <div className="flex items-center flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="p-1 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
            data-testid="dismiss-personalized"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile: Horizontal Scroll Carousel with peeking card */}
      <div className="sm:hidden -mx-3 px-3">
        <div 
          ref={scrollRef}
          className="flex gap-2.5 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2"
          style={{ 
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {listings.map((listing, index) => (
            <div
              key={listing.id}
              onClick={() => handleCardClick(listing)}
              className="flex-shrink-0 snap-start cursor-pointer"
              style={{ 
                // Width: 38% means ~2.5 cards visible, giving clear "peeking" effect on 3rd card
                width: 'calc(38% - 4px)'
              }}
            >
              <div className="group bg-card rounded-xl border border-border/50 p-2 hover:border-primary/40 hover:shadow-sm transition-all h-full">
                {/* Thumbnail */}
                <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-muted mb-2">
                  {listing.image ? (
                    <img 
                      src={listing.image} 
                      alt={listing.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Sparkles className="w-6 h-6" />
                    </div>
                  )}
                  {listing.is_boosted && (
                    <div className="absolute top-1.5 left-1.5">
                      <span className="px-1.5 py-0.5 bg-slate-800/90 text-white text-[8px] font-medium rounded-full">
                        PROMOTED
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <h4 className="text-xs font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors mb-1">
                  {listing.title}
                </h4>
                <p className="text-xs font-bold text-primary">
                  {formatPrice(listing.price, listing.currency)}
                </p>
                {listing.island && (
                  <span className="text-[9px] text-muted-foreground block truncate mt-0.5">
                    {listing.island}
                  </span>
                )}
              </div>
            </div>
          ))}
          
          {/* "See more" card at the end */}
          {listings.length >= 4 && (
            <div
              onClick={() => {
                if (displayTerm) {
                  navigate(`/marketplace?search=${encodeURIComponent(displayTerm)}`);
                }
              }}
              className="flex-shrink-0 snap-start cursor-pointer"
              style={{ width: 'calc(40% - 6px)' }}
            >
              <div className="bg-secondary/50 rounded-xl border border-border/30 p-2 h-full flex flex-col items-center justify-center text-center min-h-[120px]">
                <ChevronRight className="w-5 h-5 text-primary mb-1" />
                <span className="text-xs font-medium text-primary">
                  {t('see_all') || 'See all'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop: Compact Grid */}
      <div className="hidden sm:grid grid-cols-3 lg:grid-cols-4 gap-3">
        {listings.slice(0, 6).map((listing) => (
          <div 
            key={listing.id}
            onClick={() => handleCardClick(listing)}
            className="group relative bg-card rounded-xl border border-border/50 p-2 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer"
          >
            {/* Thumbnail */}
            <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-muted mb-2">
              {listing.image ? (
                <img 
                  src={listing.image} 
                  alt={listing.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <Sparkles className="w-6 h-6" />
                </div>
              )}
              {listing.is_boosted && (
                <div className="absolute top-1.5 left-1.5">
                  <span className="px-1.5 py-0.5 bg-slate-800/90 text-white text-[8px] font-medium rounded-full">
                    PROMOTED
                  </span>
                </div>
              )}
            </div>
            
            {/* Info */}
            <h4 className="text-xs font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors mb-1">
              {listing.title}
            </h4>
            <p className="text-xs font-bold text-primary">
              {formatPrice(listing.price, listing.currency)}
            </p>
            {listing.island && (
              <span className="text-[9px] text-muted-foreground block truncate mt-0.5">
                {listing.island}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PersonalizedRecommendations;
